/**
 * The CSV round trip of the fabrics and prices screens: the chosen file parsed
 * in the browser, replaced in bulk, logged, then the list reloaded; the export
 * downloaded as a file.
 */
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Papa from 'papaparse';
import { snackbar } from '@/shared/ui/snackbar';
import { downloadBlob } from '@/shared/lib/download';
import {
  bulkReplaceDmsItems,
  createLog,
  exportDmsCsv,
  type DmsItemType,
} from './api';

/** Delay before reloading the list after a CSV import, so CosmosDB has caught up. */
export const CSV_RELOAD_DELAY_MS = 500;
export const LOG_RETRY_DELAY_MS = 2000;

export type CsvRow = Record<string, unknown>;

export interface DmsCsvOptions {
  /** DMS item type of the rows (`fabric`, `price`). */
  type: 'fabric' | 'price';
  /** The items as shown in the messages (`fabrics`, `prices`). */
  label: string;
  logType: string;
  toItems: (rows: readonly CsvRow[]) => { type: DmsItemType; data: CsvRow }[];
  exportFileName: (now: Date) => string;
  onUploaded: () => void;
}

export const useDmsCsv = ({
  type,
  label,
  logType,
  toItems,
  exportFileName,
  onUploaded,
}: DmsCsvOptions) => {
  const [csvRows, setCsvRows] = useState<CsvRow[] | null>(null);
  const csvInput = useRef<HTMLInputElement>(null);

  /** The import log, retried like the former client did: three attempts two seconds apart. */
  const writeLog = useMutation({
    mutationFn: (filename: string) => createLog({ type: logType, filename }),
    retry: 2,
    retryDelay: LOG_RETRY_DELAY_MS,
  });

  const uploadCsv = useMutation({
    mutationFn: async () => {
      if (!csvRows) return;
      try {
        await bulkReplaceDmsItems(type, toItems(csvRows));
      } catch (error) {
        throw new Error(`Failed to update ${label}`, { cause: error });
      }
      const filename = csvInput.current?.files?.[0]?.name ?? '';
      try {
        await writeLog.mutateAsync(filename);
      } catch {
        snackbar('Failed to update the logs');
      }
      await new Promise((resolve) => setTimeout(resolve, CSV_RELOAD_DELAY_MS));
    },
    onSuccess: () => {
      if (csvInput.current) csvInput.current.value = '';
      setCsvRows(null);
      snackbar('Successfully uploaded CSV data');
      onUploaded();
    },
    onError: () => snackbar(`Failed to update ${label}`),
  });

  const downloadCsv = useMutation({
    mutationFn: () => exportDmsCsv(type),
    onSuccess: (blob) => {
      downloadBlob(blob, exportFileName(new Date()));
      snackbar('Successfully generated CSV data');
    },
    onError: () => snackbar('Erreur lors du téléchargement du CSV'),
  });

  const onCsvChosen = (file: File | undefined) => {
    if (!file) {
      setCsvRows(null);
      return;
    }
    Papa.parse<CsvRow>(file, {
      skipEmptyLines: true,
      header: true,
      complete: ({ data, errors }) => {
        if (errors.length)
          snackbar('Failed to parse some rows of the CSV file');
        setCsvRows(data);
      },
      error: () => snackbar('Failed to parse the CSV file'),
    });
  };

  return { csvRows, csvInput, uploadCsv, downloadCsv, onCsvChosen };
};
